// The JSON client. Sessions ride on the HttpOnly cookie the server sets, so
// nothing is stored here; a 401 anywhere means "signed out".

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export type Unit = { id: number; name: string; abbreviation: string; grams: number };
export type User = {
  id: string;
  email: string | null;
  username: string;
  first_name: string;
  last_name: string;
  default_unit_id: number;
  show_name_on_shared: boolean;
  needs_email_fix: boolean;
};
export type GearItem = {
  id: string;
  legacy_id: number | null;
  user_id: string;
  name: string;
  description: string;
  category: string;
  url: string;
  grams: number;
  unit_id: number;
  worn: boolean;
  consumable: boolean;
  created_at: string;
  updated_at: string;
};
export type GearList = {
  id: string;
  legacy_id: number | null;
  user_id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
};
export type GearListItem = {
  id: string;
  list_id: string;
  item_id: string;
  quantity: number;
  worn: boolean;
  checked: boolean;
  consumable: boolean;
  created_at: string;
  updated_at: string;
};
export type Totals = { total: number; worn: number; consumable: number; base: number };
export type ListDetail = { list: GearList; lines: GearListItem[]; items: GearItem[]; totals: Totals };
export type PublicList = ListDetail & { owner_name: string | null; owner_unit_id: number };

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const code = json?.error ?? "error";
    const message = json?.message ?? res.statusText;
    throw new ApiError(res.status, code, message);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

export const auth = {
  me: () => api.get<User>("/auth/me"),
  login: (login: string, password: string) => api.post<{ user: User }>("/auth/login", { login, password }),
  signup: (email: string, password: string, turnstile_token = "") =>
    api.post<{ user: User }>("/auth/signup", { email, password, turnstile_token }),
  logout: () => api.post("/auth/logout"),
  forgot: (email: string) => api.post("/auth/password/forgot", { email }),
  reset: (token: string, password: string) => api.post<{ user: User }>("/auth/password/reset", { token, password }),
  change: (current_password: string, new_password: string) =>
    api.post("/auth/password/change", { current_password, new_password }),
};

export const gear = {
  units: () => api.get<Unit[]>("/units"),
  items: () => api.get<GearItem[]>("/items"),
  createItem: (item: Partial<GearItem>) => api.post<GearItem>("/items", item),
  createItems: (items: Partial<GearItem>[]) => api.post<GearItem[]>("/items/bulk", { items }),
  updateItem: (id: string, item: Partial<GearItem>) => api.put<GearItem>(`/items/${id}`, item),
  deleteItem: (id: string) => api.delete(`/items/${id}`),
  lists: () => api.get<GearList[]>("/lists"),
  list: (id: string) => api.get<ListDetail>(`/lists/${id}`),
  createList: (list: Partial<GearList>) => api.post<GearList>("/lists", list),
  updateList: (id: string, list: Partial<GearList>) => api.put<GearList>(`/lists/${id}`, list),
  deleteList: (id: string) => api.delete(`/lists/${id}`),
  addLine: (listId: string, line: { item_id: string; quantity?: number; worn?: boolean; consumable?: boolean }) =>
    api.post<GearListItem>(`/lists/${listId}/items`, line),
  updateLine: (id: string, line: { quantity: number; worn: boolean; checked: boolean; consumable: boolean }) =>
    api.put<GearListItem>(`/list-items/${id}`, line),
  deleteLine: (id: string) => api.delete(`/list-items/${id}`),
  publicList: (id: string) => api.get<PublicList>(`/public/lists/${id}`),
  publicListByLegacy: (legacyId: number) => api.get<PublicList>(`/public/lists/by-legacy/${legacyId}`),
};

export const account = {
  update: (fields: {
    email: string | null;
    first_name: string;
    last_name: string;
    default_unit_id: number;
    show_name_on_shared: boolean;
  }) => api.put<User>("/account", fields),
  remove: (password: string) => api.delete("/account", { password }),
  support: (fields: { name: string; from_email: string; subject: string; message: string }) =>
    api.post("/support", fields),
};
